import { useCallback, useEffect, useRef, useState } from "react";
import { Apple, BellRing, Check, Loader2, Trash2, Upload } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const blank = { key_p8: "", key_id: "", team_id: "", topic: "", env: "production" };

const Field = ({ label, hint, children }) => (
  <label className="block space-y-1">
    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-500">{hint}</span>}
  </label>
);

/** Paste the Apple push key once and every iPhone notification starts working — no redeploy. */
export const ApnsPanel = ({ onNotice }) => {
  const [state, setState] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const file = useRef(null);

  const load = useCallback(async () => {
    try {
      setState((await api.get("/push/native/config")).data);
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, message) => {
    setBusy(true); setError("");
    try {
      const { data } = await fn();
      if (data?.configured !== undefined) setState({ ...state, ...data });
      if (message) onNotice?.(message);
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(() => api.put("/push/native/config", form),
    "Apple push key saved — iPhone alerts are on.");
  const drop = () => run(() => api.delete("/push/native/config"), "Apple push key removed.");
  const test = () => run(() => api.post("/push/native/test"), "Test push sent to your iPhone.");
  const ringAlert = () => run(() => api.post("/push/native/test-alert"),
    "Trip alert fired — exactly what a chauffeur gets when a job is offered.");

  const pick = (chosen) => {
    if (!chosen) return;
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, key_p8: String(reader.result) });
    reader.readAsText(chosen);
  };

  if (!state) return <Loader2 data-testid="apns-loading" className="h-5 w-5 animate-spin text-gold" />;

  return (
    <div data-testid="apns-panel" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
      <p className="flex items-center gap-2 font-display text-lg text-slate-100">
        <Apple className="h-4 w-4 text-slate-300" /> iPhone push (APNs)
      </p>
      <p className="mt-1 text-sm text-slate-400">
        An iOS app cannot receive web push at all, so the App Store builds need Apple's key. Get it
        from Apple Developer → Certificates, Identifiers & Profiles → Keys → + → Apple Push
        Notifications service. You can only download the .p8 once.
      </p>
      <p data-testid="apns-state"
        className={`mt-3 flex items-center gap-2 text-sm ${state.configured ? "text-emerald-400" : "text-amber-400"}`}>
        {state.configured ? <Check className="h-4 w-4" /> : <BellRing className="h-4 w-4" />}
        {state.configured
          ? `Live — key ${state.key_id} on team ${state.team_id} (${state.env}), from the ${state.source}. ${state.devices} device(s) registered.`
          : "Not set up yet — iPhone notifications arrive quietly until this key is in."}
      </p>
      {error && <p data-testid="apns-error" className="mt-3 text-sm text-red-400">{error}</p>}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="The .p8 key" hint="Paste the whole file, including the BEGIN and END lines.">
            <textarea data-testid="apns-key" rows={4} value={form.key_p8}
              onChange={(e) => setForm({ ...form, key_p8: e.target.value })}
              placeholder="-----BEGIN PRIVATE KEY-----&#10;…&#10;-----END PRIVATE KEY-----"
              className="w-full rounded-md border border-ink-600 bg-ink-900 px-3 py-2 font-mono text-[11px] text-slate-200" />
          </Field>
          <input ref={file} data-testid="apns-file" type="file" accept=".p8,text/plain"
            className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
          <Button data-testid="apns-upload" variant="ghost" size="sm" className="mt-2 text-slate-400"
            onClick={() => file.current?.click()}>
            <Upload className="mr-2 h-4 w-4" /> Choose the .p8 file instead
          </Button>
        </div>
        <Field label="Key ID" hint="Printed next to the key on Apple's page">
          <Input data-testid="apns-key-id" value={form.key_id} placeholder="ABCD123456"
            onChange={(e) => setForm({ ...form, key_id: e.target.value })} />
        </Field>
        <Field label="Team ID" hint="Top right of the Apple Developer site">
          <Input data-testid="apns-team-id" value={form.team_id} placeholder="WXYZ987654"
            onChange={(e) => setForm({ ...form, team_id: e.target.value })} />
        </Field>
        <Field label="Fallback bundle id" hint="Only used if a device does not report its own">
          <Input data-testid="apns-topic" value={form.topic} placeholder="com.limolinkx.chauffeur"
            onChange={(e) => setForm({ ...form, topic: e.target.value })} />
        </Field>
        <Field label="Apple environment">
          <select data-testid="apns-env" value={form.env}
            onChange={(e) => setForm({ ...form, env: e.target.value })}
            className="w-full rounded-md border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-slate-200">
            <option value="production">Production (App Store & TestFlight)</option>
            <option value="sandbox">Sandbox (Xcode debug builds)</option>
          </select>
        </Field>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button data-testid="apns-save" disabled={busy || !form.key_p8 || !form.key_id || !form.team_id}
          onClick={save} className="bg-gold text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save the key
        </Button>
        <Button data-testid="apns-test-alert" variant="outline" disabled={busy} onClick={ringAlert}>
          <BellRing className="mr-2 h-4 w-4" /> Ring a test trip alert
        </Button>
        {state.configured && (
          <>
            <Button data-testid="apns-test" variant="ghost" disabled={busy} onClick={test}
              className="text-slate-300">
              Plain test push
            </Button>
            <Button data-testid="apns-delete" variant="ghost" disabled={busy} onClick={drop}
              className="text-slate-400 hover:text-red-400">
              <Trash2 className="mr-2 h-4 w-4" /> Remove
            </Button>
          </>
        )}
      </div>
      <p data-testid="apns-devices" className="mt-3 font-mono text-[11px] text-slate-500">
        Registered for alerts —{" "}
        {Object.entries(state.devices_by_scope || {})
          .map(([scope, count]) => `${scope}: ${count} iPhone${count === 1 ? "" : "s"}`)
          .join(" · ")}
        {typeof state.web_push === "number" && ` · ${state.web_push} web/Android`}
      </p>
      <p className="mt-1 text-xs text-slate-500">
        “Ring a test trip alert” sends the exact payload a real job offer sends — urgent and
        time-sensitive, over web push and APNs — so you can prove the whole chain before a chauffeur
        depends on it.
      </p>
    </div>
  );
};
